import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ROUTE_PATH } from '../../../constants';
import { useAuth } from '../../../features/Auth';
import { getUkTodayIsoDate } from '../../../utils/dateAndTime';
import { getDefaultCollectionContactDetails } from '../../Quotes/selectors';
import { CitysprintCompleteBookingForm } from '../components/CitysprintCompleteBookingForm';
import { CitysprintCompleteBookingSummary } from '../components/CitysprintCompleteBookingSummary';
import { CitysprintFlowStepper } from '../components/CitysprintFlowStepper';
import { CitysprintLoseQuoteConfirmModal } from '../components/CitysprintLoseQuoteConfirmModal';
import { CitysprintSessionExpiredModal } from '../components/CitysprintSessionExpiredModal';
import {
  applyCitysprintCollectionContactPrefill,
  buildBookingFromParcelStep,
  buildInitialForm,
  getCitysprintContactTripletErrors,
} from '../helpers';
import { useCitysprintBackToParcelGuard } from '../hooks/useCitysprintBackToParcelGuard';
import { useCitysprintWizardExitGuard } from '../hooks/useCitysprintWizardExitGuard';
import {
  CitysprintWizardActions,
  CitysprintWizardSelectors,
  validateCitysprintQuoteSession,
} from '../redux';

const EMPTY_CONTACT_TOUCHED_RECEIVER = {
  deliveryFullName: false,
  deliveryEmail: false,
  deliveryPhone: false,
  deliveryInstructions: false,
};

const EMPTY_CONTACT_TOUCHED_YOUR = {
  collectionFullName: false,
  collectionEmail: false,
  collectionPhone: false,
  collectionInstructions: false,
};

function toCollectionContactPrefill(defaultContact) {
  if (!defaultContact) {
    return null;
  }
  const contactName = defaultContact.contactName || '';
  const email = defaultContact.email || '';
  const telephone = defaultContact.telephone || '';
  if (!contactName && !email && !telephone) {
    return null;
  }
  return { contactName, email, telephone };
}

function CompleteBookingMain({ booking, initialFormSeed }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentSession } = useAuth();
  const todayIso = useMemo(() => getUkTodayIsoDate(), []);
  const defaultCollectionContact = useSelector(
    getDefaultCollectionContactDetails
  );
  const collectionContactPrefill = useMemo(
    () =>
      currentSession?.uid
        ? toCollectionContactPrefill(defaultCollectionContact)
        : null,
    [currentSession?.uid, defaultCollectionContact]
  );

  useCitysprintWizardExitGuard();

  const [form, setForm] = useState(() => {
    const base = buildInitialForm(booking, initialFormSeed, todayIso);
    if (collectionContactPrefill) {
      return applyCitysprintCollectionContactPrefill(
        base,
        collectionContactPrefill
      );
    }
    return base;
  });

  const [receiverContactTouched, setReceiverContactTouched] = useState(
    EMPTY_CONTACT_TOUCHED_RECEIVER
  );
  const [yourContactTouched, setYourContactTouched] = useState(
    EMPTY_CONTACT_TOUCHED_YOUR
  );

  const paymentNavLockRef = useRef(false);
  const didApplyCollectionPrefillRef = useRef(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (didApplyCollectionPrefillRef.current || !collectionContactPrefill) {
      return;
    }
    didApplyCollectionPrefillRef.current = true;
    setForm(prev =>
      applyCitysprintCollectionContactPrefill(prev, collectionContactPrefill)
    );
  }, [collectionContactPrefill]);

  const persistDetails = useCallback(() => {
    dispatch(CitysprintWizardActions.patchDetailsStep(form));
  }, [dispatch, form]);

  const { confirmOpen, requestBackToParcel, handleConfirm, handleCancel } =
    useCitysprintBackToParcelGuard();

  const navigateToPaymentPreservingDetailsHistory = useCallback(async () => {
    if (paymentNavLockRef.current) {
      return;
    }
    paymentNavLockRef.current = true;
    try {
      await dispatch(validateCitysprintQuoteSession()).unwrap();
    } catch {
      paymentNavLockRef.current = false;
      dispatch(CitysprintWizardActions.clear());
      setSessionExpired(true);
      return;
    }

    persistDetails();
    navigate(ROUTE_PATH.SEND_A_CITYSPRINT_PARCEL_REVIEW_BOOK);
    paymentNavLockRef.current = false;
  }, [dispatch, navigate, persistDetails]);

  const handleBackToParcel = useCallback(() => {
    persistDetails();
    requestBackToParcel();
  }, [persistDetails, requestBackToParcel]);

  const handleNext = () => {
    void navigateToPaymentPreservingDetailsHistory();
  };

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleReceiverContactBlur = useCallback(field => {
    setReceiverContactTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const handleYourContactBlur = useCallback(field => {
    setYourContactTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const receiverContactErrors = useMemo(
    () =>
      getCitysprintContactTripletErrors(form, {
        nameKey: 'deliveryFullName',
        emailKey: 'deliveryEmail',
        phoneKey: 'deliveryPhone',
        instructionsKey: 'deliveryInstructions',
        nameRequired: true,
      }),
    [
      form.deliveryFullName,
      form.deliveryEmail,
      form.deliveryPhone,
      form.deliveryInstructions,
    ]
  );

  const yourContactErrors = useMemo(
    () =>
      getCitysprintContactTripletErrors(form, {
        nameKey: 'collectionFullName',
        emailKey: 'collectionEmail',
        phoneKey: 'collectionPhone',
        instructionsKey: 'collectionInstructions',
        nameRequired: true,
        emailRequired: true,
        phoneRequired: true,
      }),
    [
      form.collectionFullName,
      form.collectionEmail,
      form.collectionPhone,
      form.collectionInstructions,
    ]
  );

  const hasNoContactErrors = useMemo(
    () =>
      Object.keys(receiverContactErrors).length === 0 &&
      Object.keys(yourContactErrors).length === 0,
    [receiverContactErrors, yourContactErrors]
  );

  const sectionHeadingSx = { mb: 2 };

  return (
    <Box
      sx={{
        py: { xs: 2 },
        p: { md: 4 },
        background: theme.vars.palette.primary.pageBackground,
        flexGrow: 1,
      }}
    >
      <Typography variant='h1' sx={visuallyHidden}>
        Details
      </Typography>
      <Grid container spacing={0} alignItems='center' justifyContent='center'>
        <Grid
          size={{
            xs: 12,
            md: 10,
            lg: 8,
            xl: 6,
          }}
        >
          <CitysprintFlowStepper
            activeStep={1}
            maxReachableStep={hasNoContactErrors ? 2 : 1}
            onBeforeNavigate={stepKey => {
              if (stepKey !== 'payment') {
                persistDetails();
              }
            }}
            interceptNavigate={stepKey => {
              if (stepKey === 'payment') {
                void navigateToPaymentPreservingDetailsHistory();
                return true;
              }
              if (stepKey === 'parcel') {
                requestBackToParcel();
                return true;
              }
              return false;
            }}
          />

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid
              size={{
                xs: 12,
                md: 8,
              }}
            >
              <CitysprintCompleteBookingForm
                booking={booking}
                form={form}
                setField={setField}
                sectionHeadingSx={sectionHeadingSx}
                onEditAddresses={handleBackToParcel}
                receiverContactErrors={receiverContactErrors}
                receiverContactTouched={receiverContactTouched}
                onReceiverContactBlur={handleReceiverContactBlur}
                yourContactErrors={yourContactErrors}
                yourContactTouched={yourContactTouched}
                onYourContactBlur={handleYourContactBlur}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <CitysprintCompleteBookingSummary
                booking={booking}
                form={form}
                onBack={handleBackToParcel}
                onNext={handleNext}
                nextDisabled={!hasNoContactErrors}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {sessionExpired && <CitysprintSessionExpiredModal />}
      <CitysprintLoseQuoteConfirmModal
        open={confirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Box>
  );
}

const SendCitysprintParcelCompleteBooking = () => {
  const navigate = useNavigate();
  const parcelStep = useSelector(CitysprintWizardSelectors.getParcelStepValues);
  const detailsStep = useSelector(
    CitysprintWizardSelectors.getDetailsStepValues
  );

  const seedRef = useRef(null);
  if (seedRef.current === null) {
    seedRef.current = {
      booking: buildBookingFromParcelStep(parcelStep),
      initialFormSeed: detailsStep,
    };
  }
  const seed = seedRef.current;

  const hasSelection = Boolean(
    seed.booking.serviceTierId && seed.booking.vehicleKey
  );

  useLayoutEffect(() => {
    if (hasSelection) {
      return;
    }
    navigate(ROUTE_PATH.SEND_A_CITYSPRINT_PARCEL, { replace: true });
  }, [hasSelection, navigate]);

  if (!hasSelection) {
    return null;
  }

  return (
    <CompleteBookingMain
      booking={seed.booking}
      initialFormSeed={seed.initialFormSeed}
    />
  );
};

export default SendCitysprintParcelCompleteBooking;
